import { Injectable } from '@angular/core'
import { SessionNotesService } from '@ftr/api-annotations'
import { CourtroomSessionsService } from '@ftr/api-regional'
import { apiRequestWithConflict } from '@ftr/api-shared'
import { OnTheRecordTimesConflictResponse } from '@ftr/contracts/regional-api'
import { assertIsDefined } from '@ftr/contracts/shared/Assertions'
import { generateUuid, LocalTimeRange, Uuid } from '@ftr/contracts/type/shared'
import { getPlaybackId, RealTimePlaybackKey } from '@ftr/data-realtime-playback'
import {
  ConfirmationModalService,
  DateFormat,
  jodaFormatPatternWithLocale,
  ModalType,
  tapFailure,
  ToastService,
} from '@ftr/foundation'
import { LoggingService } from '@ftr/ui-observability'
import { VocabularyTermsService } from '@ftr/ui-vocab'
import { LocalDateTime, LocalTime } from '@js-joda/core'
import { Action, createSelector, Selector, State, StateContext, StateOperator } from '@ngxs/store'
import { patch } from '@ngxs/store/operators'
import { memoize } from 'lodash-es'
import { filter, firstValueFrom, lastValueFrom, Observable, of, switchMap } from 'rxjs'
import {
  getSessionMarkerId,
  getSessionMarkersFromCourtroomSessions,
  isOngoing,
  parseStartEndTimes,
  RealTimeSttSessionMarker,
} from '../../types'
import {
  mapSessionTimeValidationReasonMessage,
  SessionTimeValidation,
  SessionTimeValidationReason,
  validateSessionTimes,
} from '../../utils'
import { RealTimeSessionsConflictsModalComponent } from './real-time-sessions-conflicts-modal'
import {
  CreateSessionAction,
  DeleteSessionAction,
  ResetSessionAction,
  SetSessionMarkersAction,
  UpdateSessionAction,
  UpdateSessionLiveLocalTimeAction,
  UpdateSessionNoteAction,
  UpdateSessionRecordingEndTimeAction,
  UpdateSessionRecordingStartTimeAction,
} from './real-time-sessions.actions'
import {
  RealTimeSessionsUpdatedEvent,
  RealTimeSessionUpdateFailedEvent,
  RealTimeSessionUpdateSucceededEvent,
} from './real-time-sessions.events'
import { RealTimeSessionsModel, RealTimeSessionsStateModel } from './real-time-sessions.model'

export const DEFAULT_NEW_SESSION_LENGTH_SECONDS = 10

const defaultRealTimeSessions = (): RealTimeSessionsModel => ({
  markers: {},
  liveLocalTime: undefined,
  recordingStartTime: LocalTime.MIN,
  recordingEndTime: undefined,
  courtroomSessions: [],
  recordingDate: undefined,
  savedMarker: undefined,
})

export const defaultRealTimeSessionsState: () => RealTimeSessionsStateModel = () => ({
  realTimeSessions: {},
})

@State<RealTimeSessionsStateModel>({
  name: 'realTimeSessionsState',
  defaults: defaultRealTimeSessionsState(),
})
@Injectable()
export class RealTimeSessionsState {
  constructor(
    private readonly courtroomSessionsService: CourtroomSessionsService,
    private readonly sessionNotesService: SessionNotesService,
    private readonly toastService: ToastService,
    private readonly confirmationModalService: ConfirmationModalService,
    private readonly vocabularyTermsService: VocabularyTermsService,
    private readonly logger: LoggingService,
  ) {}

  @Selector()
  static allRealTimeSessionsStates(state: RealTimeSessionsStateModel): Record<Uuid, RealTimeSessionsModel> {
    return state.realTimeSessions
  }

  static readonly realTimeSessions = memoize(
    (
      playbackKey: RealTimePlaybackKey,
    ): ((
      sourceStates: ReturnType<typeof RealTimeSessionsState.allRealTimeSessionsStates>,
    ) => RealTimeSessionsModel) => {
      return createSelector(
        [RealTimeSessionsState.allRealTimeSessionsStates],
        (sourceStates: ReturnType<typeof RealTimeSessionsState.allRealTimeSessionsStates>) =>
          sourceStates[getPlaybackId(playbackKey)] ?? defaultRealTimeSessions(),
      )
    },
    playbackKey => getPlaybackId(playbackKey),
  )

  static readonly sessionMarkers = memoize(
    (playbackKey: RealTimePlaybackKey): ((sourceStates: RealTimeSessionsModel) => RealTimeSttSessionMarker[]) => {
      const selectedSourceState = RealTimeSessionsState.realTimeSessions(playbackKey)
      return createSelector([selectedSourceState], (state: ReturnType<typeof selectedSourceState>) => {
        if (!state?.markers) {
          return []
        }

        return Object.values(state.markers)
      })
    },
    playbackKey => getPlaybackId(playbackKey),
  )

  @Action(SetSessionMarkersAction)
  setSessionMarkers(
    stateContext: StateContext<RealTimeSessionsStateModel>,
    { playbackKey, recordingDate, courtroomSessions }: SetSessionMarkersAction,
  ): void {
    const playbackId = getPlaybackId(playbackKey)
    const { liveLocalTime, savedMarker } = getOrInitializeRealTimeSessions(stateContext, playbackId)

    const markers = getSessionMarkersFromCourtroomSessions(courtroomSessions).reduce<
      Record<string, RealTimeSttSessionMarker>
    >((acc, marker) => {
      const isSavedMarker = savedMarker?.id === marker.id
      // Set the status and the source of the updated marker
      acc[marker.id] = {
        ...marker,
        status: isSavedMarker ? 'saved' : marker.status,
        source: isSavedMarker ? savedMarker?.source : marker.source,
        showAsOngoing: isOngoing(liveLocalTime, marker.sessionTimes),
      }

      return acc
    }, {})

    stateContext.setState(
      patchSessions(playbackId, { markers, courtroomSessions, recordingDate, savedMarker: undefined }),
    )
  }

  @Action(UpdateSessionRecordingStartTimeAction)
  updateSessionRecordingStartTime(
    { setState }: StateContext<RealTimeSessionsStateModel>,
    { playbackKey, recordingStartTime }: UpdateSessionRecordingStartTimeAction,
  ): void {
    setState(patchSessions(getPlaybackId(playbackKey), { recordingStartTime }))
  }

  @Action(UpdateSessionRecordingEndTimeAction)
  updateSessionRecordingEndTime(
    { setState }: StateContext<RealTimeSessionsStateModel>,
    { playbackKey, recordingEndTime }: UpdateSessionRecordingEndTimeAction,
  ): void {
    setState(patchSessions(getPlaybackId(playbackKey), { recordingEndTime }))
  }

  @Action(UpdateSessionLiveLocalTimeAction)
  updateLiveLocalTime(
    stateContext: StateContext<RealTimeSessionsStateModel>,
    { playbackKey, liveLocalTime }: UpdateSessionLiveLocalTimeAction,
  ): void {
    const playbackId = getPlaybackId(playbackKey)
    const { markers } = getOrInitializeRealTimeSessions(stateContext, playbackId)

    stateContext.setState(
      patchSessions(getPlaybackId(playbackKey), {
        liveLocalTime,
        markers: Object.fromEntries(
          Object.entries(markers ?? {}).map(([id, marker]) => [
            id,
            {
              ...marker,
              showAsOngoing: isOngoing(liveLocalTime, marker.sessionTimes),
            },
          ]),
        ),
      }),
    )
  }

  @Action(CreateSessionAction)
  createSession(
    stateContext: StateContext<RealTimeSessionsStateModel>,
    { playbackKey, startTime, courtSystemId }: CreateSessionAction,
  ): Observable<void> {
    const { courtroomSessions } = getOrInitializeRealTimeSessions(stateContext, getPlaybackId(playbackKey))
    startTime = startTime.withNano(0)
    const timeRanges = courtroomSessions.map(
      x => new LocalTimeRange(x.startTime.toLocalTime(), x.endTime?.toLocalTime() ?? LocalTime.MAX.withNano(0)),
    )
    if (timeRanges.some(x => x.contains(startTime.toLocalTime()))) {
      this.toastService.error('Session already exists at that time.')
      return of()
    }
    const nextSession = courtroomSessions
      .toSorted((a, b) => a.startTime.compareTo(b.startTime))
      .find(x => x.startTime.isAfter(startTime))

    const maxEnd = nextSession?.startTime ?? LocalDateTime.of(startTime.toLocalDate(), LocalTime.MAX.withNano(0))
    const defaultEnd = startTime.plusSeconds(DEFAULT_NEW_SESSION_LENGTH_SECONDS)
    const endTime: LocalDateTime = defaultEnd.isBefore(maxEnd) ? defaultEnd : maxEnd
    const recordingId = playbackKey.recordingId
    assertIsDefined(recordingId, 'Expected RealTimePlaybackKey to include recordingId')

    return this.courtroomSessionsService.addCourtroomSession(courtSystemId, recordingId, startTime, endTime).pipe(
      tapFailure(() => this.toastService.error('Error adding session.')),
      filter(x => x.isSuccess()),
      switchMap(() => stateContext.dispatch(new RealTimeSessionsUpdatedEvent(playbackKey))),
    )
  }

  @Action(DeleteSessionAction)
  async deleteSession(
    { dispatch }: StateContext<RealTimeSessionsStateModel>,
    { playbackKey, timeRange, courtSystemId, courtroomSessionId }: DeleteSessionAction,
  ): Promise<void> {
    const recordingId = playbackKey.recordingId
    assertIsDefined(recordingId, 'Expected RealTimePlaybackKey to include recordingId')

    const terms = await firstValueFrom(this.vocabularyTermsService.observeTerms(courtSystemId))
    const timeFormat = jodaFormatPatternWithLocale(DateFormat.TimeWithSeconds)

    return apiRequestWithConflict({
      responseConstructor: OnTheRecordTimesConflictResponse,
      request: (conflictToken: string | undefined) =>
        this.courtroomSessionsService.deleteCourtroomSession(
          courtroomSessionId,
          courtSystemId,
          recordingId,
          conflictToken,
        ),
      onSuccess: () => lastValueFrom(dispatch(new RealTimeSessionsUpdatedEvent(playbackKey))),
      onError: () => {
        this.toastService.error('Error deleting session.')
      },
      onConflict: (
        responseBody: OnTheRecordTimesConflictResponse,
        hasPreviousConflictResolutionTokenExpired: boolean,
      ) => {
        return this.confirmationModalService.confirm({
          modalType: ModalType.Warning,
          title: 'Delete Session',
          confirmText: 'Delete Session',
          confirmButtonColor: 'danger',
          content: {
            type: RealTimeSessionsConflictsModalComponent,
            inputs: {
              title: `Deleting this session from ${timeRange.start.format(timeFormat)} to ${timeRange.end.format(timeFormat)} will affect the following:`,
              terms,
              timeRange,
              conflicts: responseBody.conflicts,
              hasPreviousConflictResolutionTokenExpired,
            },
          },
        })
      },
    })
  }

  @Action(UpdateSessionAction)
  async updateSession(
    stateContext: StateContext<RealTimeSessionsStateModel>,
    action: UpdateSessionAction,
  ): Promise<void> {
    const { playbackKey, markerId, startTime, endTime, courtSystemId, courtroomSessionId, source, departmentId } =
      action

    const playbackId = getPlaybackId(playbackKey)
    const { markers, courtroomSessions, liveLocalTime, recordingStartTime, recordingEndTime, recordingDate } =
      getOrInitializeRealTimeSessions(stateContext, playbackId)
    const { dispatch, setState } = stateContext
    const marker = markers[markerId]
    const existing = marker?.sessionTimes

    if (!marker || !existing || !recordingDate) {
      return
    }

    const parseResult = parseStartEndTimes<SessionTimeValidationReason>({ startTime, endTime })
    if ('parseError' in parseResult) {
      this.handleSessionErrors(action, marker, parseResult.parseError, stateContext)
      return
    }

    const updated = new LocalTimeRange(parseResult.startTime, parseResult.endTime)

    // Check if the times are unchanged
    const areTimesUnchanged = updated.equals(existing)
    if (departmentId === marker.departmentId && areTimesUnchanged) {
      dispatch(new ResetSessionAction(playbackKey, courtroomSessionId))
      return
    }

    // Check if the times are within the recording start and end time
    if (!areTimesUnchanged) {
      const timeValidationError = validateSessionTimes(
        parseResult.startTime,
        parseResult.endTime,
        courtroomSessionId,
        courtroomSessions,
        recordingStartTime,
        recordingEndTime,
        liveLocalTime,
        true,
      )

      if (timeValidationError) {
        this.handleSessionErrors(action, marker, timeValidationError, stateContext)
        return
      }
    }

    setState(
      patchSessionMarker(playbackId, markerId, {
        status: 'saving',
        source,
      }),
    )

    const recordingId = playbackKey.recordingId
    assertIsDefined(recordingId, 'Expected RealTimePlaybackKey to include recordingId')

    return apiRequestWithConflict({
      responseConstructor: OnTheRecordTimesConflictResponse,
      request: (conflictToken: string | undefined) =>
        this.courtroomSessionsService.updateCourtroomSession(
          courtroomSessionId,
          courtSystemId,
          recordingId,
          LocalDateTime.of(recordingDate, updated.start),
          LocalDateTime.of(recordingDate, updated.end),
          departmentId,
          conflictToken,
        ),

      onSuccess: () => {
        // Updating savedMarker with its future id just before reloading the recording
        if (recordingDate) {
          setState(
            patchSessions(playbackId, {
              savedMarker: { id: getSessionMarkerId(courtroomSessionId, marker.dividerType), source },
            }),
          )
        }
        dispatch(new RealTimeSessionUpdateSucceededEvent(action))
        return lastValueFrom(dispatch(new RealTimeSessionsUpdatedEvent(playbackKey)))
      },
      onError: error => {
        this.logger.error({ message: 'Failed to update session', error })
        setState(
          patchSessionMarker(playbackId, markerId, {
            status: { errorMessage: 'Failed to save.', showRetry: true, showReset: false },
            source,
          }),
        )
        dispatch(new RealTimeSessionUpdateFailedEvent(action, 'Failed to save.', false, true))
      },
      onCancel: () => {
        dispatch(new ResetSessionAction(playbackKey, courtroomSessionId))
      },
      onConflict: async (
        responseBody: OnTheRecordTimesConflictResponse,
        hasPreviousConflictResolutionTokenExpired: boolean,
      ) => {
        const terms = await firstValueFrom(this.vocabularyTermsService.observeTerms(courtSystemId))
        const timeFormat = jodaFormatPatternWithLocale(DateFormat.TimeWithSeconds)

        return this.confirmationModalService.confirm({
          modalType: ModalType.Warning,
          title: 'Update Session Times',
          confirmText: 'Update Session Times',
          confirmButtonColor: 'primary',
          content: {
            type: RealTimeSessionsConflictsModalComponent,
            inputs: {
              title: `Updating this session time ${updated.start.format(timeFormat)} to ${updated.end.format(timeFormat)} will affect the following:`,
              terms,
              timeRange: existing,
              conflicts: responseBody.conflicts,
              hasPreviousConflictResolutionTokenExpired,
            },
          },
        })
      },
    })
  }

  @Action(ResetSessionAction)
  async resetSessionAction(
    { getState, setState }: StateContext<RealTimeSessionsStateModel>,
    { playbackKey, sessionId }: ResetSessionAction,
  ): Promise<void> {
    const playbackId = getPlaybackId(playbackKey)

    Object.values(getState().realTimeSessions[playbackId]?.markers ?? {})
      .filter(marker => marker.courtroomSessionId === sessionId)
      .forEach(affectedMarker => {
        setState(
          patchSessionMarker(playbackId, affectedMarker.id, { status: 'loaded', forceResetFormKey: generateUuid() }),
        )
      })
  }

  @Action(UpdateSessionNoteAction)
  async updateSessionNote(
    _stateContext: StateContext<RealTimeSessionsStateModel>,
    action: UpdateSessionNoteAction,
  ): Promise<void> {
    const { recordingId, courtSystemId, courtroomSessionId, noteText } = action

    if (!recordingId) {
      return
    }

    this.sessionNotesService
      .updateSessionNote(courtSystemId, recordingId, courtroomSessionId, noteText)
      .pipe(
        tapFailure(() => this.toastService.error('Error updating session note.')),
        filter(x => x.isSuccess()),
      )
      .subscribe()

    return
  }

  private handleSessionErrors(
    action: UpdateSessionAction,
    marker: RealTimeSttSessionMarker,
    validationResult: SessionTimeValidation,
    { setState, dispatch }: StateContext<RealTimeSessionsStateModel>,
  ): void {
    const errorMessage = mapSessionTimeValidationReasonMessage(validationResult)
    const updatedMarker = {
      ...marker,
      status: {
        errorMessage,
        fields: mapValidationReasonToFields(validationResult.reason),
        showRetry: false,
        showReset: true,
      },
      source: action.source,
    }

    setState(patchSessionMarker(getPlaybackId(action.playbackKey), marker.id, updatedMarker))
    dispatch(new RealTimeSessionUpdateFailedEvent(action, errorMessage, true, false))
  }
}

function patchSessions(id: Uuid, changes: Partial<RealTimeSessionsModel>): StateOperator<RealTimeSessionsStateModel> {
  return patch({
    realTimeSessions: patch({
      [id]: patch(changes),
    }),
  })
}

function patchSessionMarker(
  playbackId: Uuid,
  markerId: string,
  changes: Partial<RealTimeSttSessionMarker>,
): StateOperator<RealTimeSessionsStateModel> {
  return patch({
    realTimeSessions: patch({
      [playbackId]: patch({
        markers: patch({
          [markerId]: patch(changes),
        }),
      }),
    }),
  })
}

function getOrInitializeRealTimeSessions(
  { getState, setState }: StateContext<RealTimeSessionsStateModel>,
  playbackId: Uuid,
): RealTimeSessionsModel {
  const originalState = getState()

  let realTimeSessions = originalState.realTimeSessions[playbackId]
  if (!realTimeSessions) {
    realTimeSessions = defaultRealTimeSessions()
    setState(patchSessions(playbackId, realTimeSessions))
  }

  return realTimeSessions
}

function mapValidationReasonToFields(
  validationResult: SessionTimeValidationReason,
): (keyof RealTimeSttSessionMarker)[] {
  const affectsAll = ['both-out-of-bounds', 'already-exists', 'start-must-be-before-end', 'both-are-not-times']
  const startIsInvalid = ['start-is-out-of-bounds', 'start-is-not-a-time', ...affectsAll].includes(validationResult)
  const endIsInvalid = [
    'end-is-out-of-bounds',
    'future-end-time',
    'end-is-not-a-time',
    'end-time-update-for-live-session',
    ...affectsAll,
  ].includes(validationResult)
  const fields: (keyof RealTimeSttSessionMarker)[] = []
  if (startIsInvalid) {
    fields.push('startTime')
  }
  if (endIsInvalid) {
    fields.push('endTime')
  }
  return fields
}
